import { useEffect, useState } from 'react';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import type { ProductsSortKey, ProductTypeFilter } from './api';
import {
  DEFAULT_PRODUCTS_SORT,
  toggleProductsSort,
  type ProductsSort,
} from './hooks';

/**
 * URL-backed search + sort state for the product list pages.
 *
 * The URL is the source of truth (`?q=stent&sort=name&dir=asc`): refresh
 * keeps the view, back/forward walk through it, and a pasted link shows a
 * teammate the exact same filtered table. No browser storage involved —
 * project rule. The text input stays a local controlled value so typing
 * is instant; it debounces INTO the URL, and the server query reads the
 * URL.
 */

export interface ProductListSearch {
  q?: string;
  sort?: ProductsSortKey;
  dir?: 'asc' | 'desc';
  /** Roster type filter. Absent = all types (there is deliberately no
   *  ``type=all`` in the URL — "no filter" is the absence of the param). */
  type?: ProductTypeFilter;
}

const PRODUCT_TYPE_FILTERS: readonly ProductTypeFilter[] = ['device', 'drug', 'biologic'];

/** `validateSearch` for the six list routes — unknown/malformed params are
 *  dropped rather than erroring (a hand-edited URL degrades to defaults). */
export function validateProductListSearch(
  search: Record<string, unknown>,
): ProductListSearch {
  const out: ProductListSearch = {};
  if (typeof search.q === 'string' && search.q.trim()) {
    out.q = search.q.slice(0, 200);
  }
  if (search.sort === 'submitted' || search.sort === 'name' || search.sort === 'type') {
    out.sort = search.sort;
  }
  if (search.dir === 'asc' || search.dir === 'desc') {
    out.dir = search.dir;
  }
  if (PRODUCT_TYPE_FILTERS.includes(search.type as ProductTypeFilter)) {
    out.type = search.type as ProductTypeFilter;
  }
  return out;
}

/** Effective sort for a validated search — absent params mean the server
 *  default (newest first; name/type start ascending). */
export function sortFromSearch(search: ProductListSearch): ProductsSort {
  const key = search.sort ?? DEFAULT_PRODUCTS_SORT.key;
  const dir = search.dir ?? (key === 'submitted' ? 'desc' : 'asc');
  return { key, dir };
}

/** The sort the list is actually fetched with. The archived list reads "Archived" in
 *  every Status cell and its Status header doesn't sort, so a Status sort carried over
 *  from the active list (it lives in the URL, shared by both) would order the rows by a
 *  status nobody can see, with no arrow to show or undo it: the archived list uses the
 *  default order instead. The URL keeps the Status sort for when the user switches back. */
export function sortForList(sort: ProductsSort, archived: boolean): ProductsSort {
  return archived && sort.key === 'status' ? DEFAULT_PRODUCTS_SORT : sort;
}

type NavigateFn = (opts: {
  search: (prev: ProductListSearch) => ProductListSearch;
  replace?: boolean;
}) => void | Promise<void>;

export function useProductListControls(
  searchParams: ProductListSearch,
  navigate: NavigateFn,
) {
  const urlQ = searchParams.q ?? '';
  const sort = sortFromSearch(searchParams);
  const [searchInput, setSearchInput] = useState(urlQ);
  const debouncedQ = useDebouncedValue(searchInput.trim(), 300);

  // Typing → URL. `replace` so each keystroke doesn't grow history — the
  // back button walks pages, not letters.
  useEffect(() => {
    if (debouncedQ !== urlQ) {
      void navigate({
        search: (prev) => ({ ...prev, q: debouncedQ || undefined }),
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  // URL → input (back/forward, pasted links). Guarded against the debounce
  // window so it can't clobber keystrokes that haven't flushed yet.
  useEffect(() => {
    if (urlQ !== debouncedQ) setSearchInput(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQ]);

  // Type filter → URL. Not debounced (it's a discrete pick, not typing) and
  // NOT `replace`, so back undoes a filter change the way a user expects.
  const productType = searchParams.type;
  const onProductType = (next: ProductTypeFilter | undefined) => {
    void navigate({ search: (prev) => ({ ...prev, type: next }) });
  };

  const onSort = (key: ProductsSortKey) => {
    const next = toggleProductsSort(sort, key);
    const isDefault =
      next.key === DEFAULT_PRODUCTS_SORT.key && next.dir === DEFAULT_PRODUCTS_SORT.dir;
    void navigate({
      search: (prev) => ({
        ...prev,
        sort: isDefault ? undefined : next.key,
        dir: isDefault ? undefined : next.dir,
      }),
    });
  };

  return {
    /** Raw input value (instant) — bind to the search box. */
    searchInput,
    setSearchInput,
    /** The URL's q — feed this to the server query. */
    q: urlQ,
    sort,
    onSort,
    /** The URL's type filter — feed this to the server query. */
    productType,
    onProductType,
    // Drives the "no results" vs "empty roster" split: a filtered-to-nothing
    // view must not show the "register your first product" empty state.
    filtering: urlQ !== '' || productType !== undefined,
  };
}
